import {ApiNote} from 'app/modules/Remote/Feature/types';

export type ApiExploreRunScene = {
  sourceId: string;
  layerId: string;
  sensingTime: string;
};

// Prompt half of the start-run body: a preset key or custom free text, never both. agentType routes
// custom text -- 'identify' segments it as a concept list, 'describe' (the default) sends it to the
// LLM analysis agent.
export type ExploreRunAgentType = 'describe' | 'identify';
export type ExploreRunPrompt =
  // fewShot carries the drawn exemplars for the "Find more like this" preset.
  | {
      promptKey: string;
      fewShot?: {
        label: string;
        exemplars: GeoJSON.Polygon[];
        negativeExemplars: GeoJSON.Polygon[];
        searchTerm: string;
        negativeLabel: string;
      };
      userPrompt?: never;
      agentType?: never;
    }
  | {promptKey?: never; userPrompt: string; agentType?: ExploreRunAgentType; fewShot?: never};

// How much imagery a run reads, which also picks the wording of every preset prompt. A
// single-feature run gets it from the scenes picked on the map; a run plan states it up front and
// each of its runs resolves its own scenes.
export type AgentSceneMode = 'single' | 'compare';

export const isRunInFlight = (status: ApiExploreRun['status']) =>
  status === 'pending' || status === 'in_progress';

export type ApiExploreRun = {
  id: number;
  featureId: number;
  featureName: string;
  // Null when the feature has no primary project.
  lensUrl: string | null;
  createdBy: string;
  config: {
    user_prompt: string;
    // Preset prompt tag; null for custom free-text prompts.
    prompt_key: string | null;
    // Concept(s) segmented by a SAM run; absent on LLM analysis runs.
    sam_prompt?: string | string[];
    // "Find more like this" exemplars (WGS84) and the user's label for them; absent on every
    // other run type.
    sam_fewshot_exemplars?: GeoJSON.Polygon[];
    sam_fewshot_negative_exemplars?: GeoJSON.Polygon[];
    sam_fewshot_search_term?: string;
    // Runs from before sam_fewshot_search_term searched with the note label when this was on.
    sam_fewshot_ground_refine_with_label?: boolean;
    sam_fewshot_negative_label?: string;
    sam_observation_label?: string;
  };
  scenes: ApiExploreRunScene[];
  logs: string;
  // A batch run reaches `cancelled` when its run plan or run was deleted while it was still
  // going; the row stays readable so a note the run wrote keeps its provenance.
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  endedAt: string | null;
  notes: ApiNote[];
};
